import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import FetchWrapper from "@/components/UI/FetchWraper";
import type { SiteInfo } from "@/components/UI/SitesMap/types";
import { useScopeParams } from "@/hooks/useScopeParams";
import { apiFetchJson } from "@/utils/apiFetch";
import SiteSidebar from "./SiteSidebar";
import SingleSiteCardHeader from "./SingleSiteCardHeader";
import { createSiteMenuItems, createContentMap } from "./utils/SiteTabItems";
import { useRoleFilter } from "@/hooks/useRoleFilter";
import useIsFeatureFlagEnabled from "@/hooks/useIsFeatureFlagEnabled";
import { getAvailableMenuKeys } from "@/utils/landingPageUtils";
import type { Investigator } from "@/views/InvestigatorProfile/types";

// Same payload used by MapTab — fetch all sites, filter client-side.
// Isolates the reconciliation point: swap for a per-site endpoint when ready.
const ALL_SITES_PAYLOAD = {
  dataset_type: "sites-overview",
  global_filters: [],
  pagination: { page: 1, page_size: 10000 },
};

export default function SingleSiteOverview() {
  return (
    <FetchWrapper
      endpointSuffix="filters/data/"
      method="POST"
      body={ALL_SITES_PAYLOAD}
    >
      {(data) => (
        <SingleSiteOverviewContent
          allSitesData={Array.isArray(data?.results) ? data.results : []}
        />
      )}
    </FetchWrapper>
  );
}

function SingleSiteOverviewContent({
  allSitesData,
}: {
  allSitesData: SiteInfo[];
}) {
  const { siteId, buildUrl } = useScopeParams();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  const [activeTab, setActiveTab] = useState("overview");
  const { filterTabGroups } = useRoleFilter();

  const siteInfo = useMemo(
    () => allSitesData.find((s) => s.site === siteId) ?? null,
    [allSitesData, siteId],
  );

  const portfolioSiteKey = siteInfo?.golden_site_id ?? null;

  // Fetch portfolio investigators only when a golden_site_id is available.
  // Matches investigator to this site via site_key to surface the canonical profile link.
  const { data: investigators = [] } = useQuery({
    queryKey: ["portfolio-investigators"],
    queryFn: () =>
      apiFetchJson<Investigator[]>(
        `${import.meta.env.VITE_API_URL}/data/portfolio/portfolio-investigators/`,
      ),
    staleTime: 5 * 60 * 1000,
    enabled: !!portfolioSiteKey,
  });

  const invKey = useMemo(
    () =>
      portfolioSiteKey
        ? (investigators.find((inv) => inv.site_key === portfolioSiteKey)
            ?.inv_key ?? null)
        : null,
    [investigators, portfolioSiteKey],
  );

  const sitesList = useMemo(
    () =>
      allSitesData.map((s) => ({
        value: s.site,
        label: s.site_name ? `${s.site_name} (${s.site})` : s.site,
      })),
    [allSitesData],
  );
  const isFlagEnabled = useIsFeatureFlagEnabled();

  const sidebarMenuItems = useMemo(
    () => filterTabGroups(createSiteMenuItems(isFlagEnabled) as any[]),
    [filterTabGroups, isFlagEnabled],
  );
  const availableTabKeys = useMemo(
    () => (getAvailableMenuKeys(sidebarMenuItems) ?? []) as any[],
    [sidebarMenuItems],
  );
  const firstAvailableTab = availableTabKeys[0] ?? "overview";
  const contentMap = createContentMap(siteId, siteInfo, invKey);

  useEffect(() => {
    const segments = path.split("/");
    const siteIdIndex = segments.indexOf(siteId);
    const requestedTab = segments[siteIdIndex + 1] || "";
    const nextTab =
      requestedTab && availableTabKeys.includes(requestedTab)
        ? requestedTab
        : firstAvailableTab;
    setActiveTab(nextTab);
    if ((!requestedTab || requestedTab !== nextTab) && nextTab) {
      navigate(buildUrl(`sites/profile/${siteId}/${nextTab}`), {
        replace: true,
      });
    }
  }, [siteId, path, availableTabKeys, firstAvailableTab, navigate, buildUrl]);

  const handleSiteChange = (newSiteId: string) => {
    navigate(buildUrl(`sites/profile/${newSiteId}/${activeTab}`));
  };

  const handleMenuSelect = (key: string) => {
    setActiveTab(key);
    navigate(buildUrl(`sites/profile/${siteId}/${key}`));
  };

  return (
    <div className="flex h-full">
      <SiteSidebar
        sitesList={sitesList}
        menuItems={sidebarMenuItems}
        activeKey={activeTab}
        currentSiteId={siteId}
        onSiteChange={handleSiteChange}
        onMenuSelect={handleMenuSelect}
        portfolioSiteKey={portfolioSiteKey}
      />
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        <div className="px-4 pt-3 pb-2">
          <SingleSiteCardHeader siteInfo={siteInfo} />
        </div>
        <div className="flex-1 min-h-0 p-4 overflow-y-auto">
          {contentMap[activeTab] ?? contentMap["overview"]}
        </div>
      </div>
    </div>
  );
}
